import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Dialog,
  TextField,
  Snackbar,
  IconButton,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Edit, Delete, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from 'src/apiConfig';
import { latinToCyrillic } from 'src/helper/helperFunction';
import { useLocaleText } from 'src/helper/localeText';

/* ADMIN STRANA ZA LOKACIJE*/

function LocationManager() {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const localeText = useLocaleText();

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/locations.php`);
      setLocations(response.data);
      setFilteredLocations(response.data);
    } catch (error) {
      console.error(t('location.errorFetchingLocations'), error);
    }
  };

  // Funkcija za pretragu sa latinicom i ćirilicom
  useEffect(() => {
    const searchQueryLatin = latinToCyrillic(searchQuery.toLowerCase());
    const result = locations.filter(
      (location) =>
        location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.name.toLowerCase().includes(searchQueryLatin) ||
        location.area.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.area.toLowerCase().includes(searchQueryLatin) ||
        location.zipcode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.zipcode.toLowerCase().includes(searchQueryLatin)
    );
    setFilteredLocations(result);
  }, [searchQuery, locations]);

  const handleAddOrUpdate = async () => {
    try {
      if (selectedLocation?.isEditing) {
        await axios.put(
          `${API_BASE_URL}/api/locations.php?zipcode=${selectedLocation.zipcode}`,
          {
            name: selectedLocation.name,
            area: selectedLocation.area
          }
        );
        setSnackbarMessage(t('location.locationUpdated'));
      } else {
        await axios.post(`${API_BASE_URL}/api/locations.php`, {
          zipcode: selectedLocation.zipcode,
          name: selectedLocation.name,
          area: selectedLocation.area
        });
        setSnackbarMessage(t('location.locationAdded'));
      }
      fetchLocations();
      setOpenDialog(false);
      setSnackbarOpen(true);
    } catch (error) {
      console.error(t('location.errorSavingLocation'), error);
    }
  };

  const handleEdit = (location) => {
    setSelectedLocation({ ...location, isEditing: true });
    setOpenDialog(true);
  };

  const handleAdd = () => {
    setSelectedLocation({ zipcode: '', name: '', area: '', isEditing: false });
    setOpenDialog(true);
  };

  const handleDelete = async (zipcode) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/locations.php?zipcode=${zipcode}`
      );
      setSnackbarMessage(t('location.locationDeleted'));
      fetchLocations();
      setSnackbarOpen(true);
    } catch (error) {
      console.error(t('location.errorDeletingLocation'), error);
    }
  };

  const columns = [
    { field: 'zipcode', headerName: t('location.zipcode'), width: 150 },
    { field: 'area', headerName: t('location.area'), width: 200 },
    { field: 'name', headerName: t('location.name'), width: 250 },
    {
      field: 'actions',
      headerName: t('location.actions'),
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() => handleEdit(params.row)}
            sx={{
              color: '#2596be',
              '&:hover': {
                backgroundColor: '#e0f7fa',
                borderRadius: '50%'
              }
            }}
          >
            <Edit sx={{ color: '#1c75bc', fontSize: '24px' }} />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(params.row.zipcode)}
            sx={{
              color: '#2596be',
              '&:hover': {
                backgroundColor: '#e0f7fa',
                borderRadius: '50%'
              }
            }}
          >
            <Delete sx={{ color: '#e91e63', fontSize: '24px' }} />
          </IconButton>
        </>
      ),
      width: 150
    }
  ];

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: '#f5f5f5',
        borderRadius: '15px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        maxWidth: '1800px' // Širi boks
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ color: '#2596be' }}>
        {t('location.manageLocations')}
      </Typography>

      <TextField
        variant="outlined"
        placeholder={t('search.placeholder')}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          )
        }}
        sx={{
          width: '100%',
          backgroundColor: '#fff',
          borderRadius: '10px',
          marginBottom: '20px'
        }}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleAdd}
        sx={{
          marginBottom: 2,
          backgroundColor: '#2596be',
          borderRadius: '20px',
          '&:hover': {
            backgroundColor: '#1976d2'
          }
        }}
      >
        {t('location.addLocation')}
      </Button>

      <DataGrid
        rows={filteredLocations}
        columns={columns.map((column) => ({ ...column, flex: 1 }))}
        pageSize={10}
        rowsPerPageOptions={[10, 20, 50]}
        getRowId={(row) => row.zipcode}
        autoHeight
        pagination
        disableColumnMenu
        localeText={{
          ...localeText,
          noRowsLabel: t('pagination.noRowsLabel')
        }}
        componentsProps={{
          pagination: {
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${t('pagination.of')} ${
                count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
              }`,
            showFirstButton: true,
            showLastButton: true
          }
        }}
        sx={{
          boxShadow: 3,
          border: 2,
          borderColor: '#2596be',
          borderRadius: '15px',
          backgroundColor: 'white',
          '& .MuiDataGrid-root': {
            border: 'none',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '10px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
          },
          '& .MuiDataGrid-cell:hover': {
            color: '#1976d2'
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#2596be',
            color: 'white',
            fontSize: '16px',
            borderTopLeftRadius: '15px',
            borderTopRightRadius: '15px'
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            padding: '12px',
            fontSize: '14px',
            color: '#333'
          },
          '& .MuiDataGrid-footerContainer': {
            borderBottomLeftRadius: '15px',
            borderBottomRightRadius: '15px',
            backgroundColor: '#e0f7fa'
          }
        }}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm" // Povećaj širinu dijaloga
        fullWidth // Da dijalog zauzima maksimalnu širinu
        sx={{
          '& .MuiDialog-paper': {
            padding: 3,
            borderRadius: '20px',
            backgroundColor: '#f5f5f5',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <Typography variant="h6" sx={{ color: '#2596be' }}>
          {t('location.locationDetails')}
        </Typography>
        <TextField
          label={t('location.zipcode')}
          value={selectedLocation?.zipcode || ''}
          onChange={(e) =>
            setSelectedLocation({
              ...selectedLocation,
              zipcode: e.target.value
            })
          }
          fullWidth
          margin="normal"
          sx={{
            backgroundColor: 'white',
            borderRadius: '10px'
          }}
        />
        <TextField
          label={t('location.area')}
          value={selectedLocation?.area || ''}
          onChange={(e) =>
            setSelectedLocation({
              ...selectedLocation,
              area: e.target.value
            })
          }
          fullWidth
          margin="normal"
          sx={{
            backgroundColor: 'white',
            borderRadius: '10px'
          }}
        />
        <TextField
          label={t('location.name')}
          value={selectedLocation?.name || ''}
          onChange={(e) =>
            setSelectedLocation({
              ...selectedLocation,
              name: e.target.value
            })
          }
          fullWidth
          margin="normal"
          sx={{
            backgroundColor: 'white',
            borderRadius: '10px'
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddOrUpdate}
          sx={{
            marginTop: 2,
            backgroundColor: '#2596be',
            borderRadius: '20px',
            '&:hover': {
              backgroundColor: '#1976d2'
            }
          }}
        >
          {t('location.save')}
        </Button>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#2596be',
            borderRadius: '10px',
            color: 'white'
          }
        }}
      />
    </Box>
  );
}

export default LocationManager;
